import { useCallback, useEffect, useState } from 'react'

import { MdDelete, MdEdit } from 'react-icons/md'
import { useInfiniteQuery, useQuery } from 'react-query'

import { Page } from '@/components/layout/page/page'
import { Button } from '@/components/ui/button'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { openDialog } from '@/models/dialogs.ts'
import { AppRoutes } from '@/utils/constants'
import { handleError } from '@/utils/error.ts'
import {
  TrackedFeatures,
  trackFeatureUsage,
} from '@/utils/feature-engagement.ts'

import { getListById, getListMedias } from '../../service'
import { TikTokListDetailsSkeleton } from '../../skeletons/TikTokListDetailsSkeleton.tsx'
import type { ListMediaType } from '../../types'
import { ListLabel } from '../../videos/ui/ListLabel.tsx'
import type { SelectedListItemsMap } from '../types'
import { ListItemsGroup } from '../ui/ListItemsGroup.tsx'
import { SelectedItemsActions } from '../ui/SelectedItemsActions.tsx'

const initialSelectedItems: SelectedListItemsMap = {
  sounds: new Map(),
  posts: new Map(),
}

interface Props {
  listId: string
}

export const TikTokListDetailsView = ({ listId }: Props) => {
  const {
    isFetching,
    data: list,
    refetch,
  } = useQuery(['list', listId], () => getListById(listId), {
    onError: (error) => handleError(error, 'Failed to get list', refetch),
  })

  const [selectedItems, setSelectedItems] =
    useState<SelectedListItemsMap>(initialSelectedItems)

  const postsQuery = useInfiniteQuery(
    ['listMedia', listId, 'posts'],
    ({ pageParam = 1 }) => getListMedias(listId, 'posts', pageParam),
    {
      getNextPageParam: (page, pages) => {
        return page.last ? undefined : pages.length + 1
      },
      refetchOnMount: true,
    },
  )

  const soundsQuery = useInfiniteQuery(
    ['listMedia', listId, 'sounds'],
    ({ pageParam = 1 }) => getListMedias(listId, 'sounds', pageParam),
    {
      getNextPageParam: (page, pages) =>
        page.last ? undefined : pages.length + 1,
      refetchOnMount: true,
      staleTime: 500,
    },
  )

  useEffect(() => {
    if (
      postsQuery.data?.pages[0]?.content.length ||
      soundsQuery.data?.pages[0]?.content.length
    ) {
      trackFeatureUsage(TrackedFeatures.TiktokResearch)
    }
  }, [postsQuery.data, soundsQuery.data])

  const handleSelectItem = useCallback(
    (type: ListMediaType, id: number) => {
      setSelectedItems((prev) => {
        const newMap = new Map(prev[type])

        newMap.set(id, !prev[type].get(id))

        return { ...prev, [type]: newMap }
      })
    },
    [setSelectedItems],
  )

  const resetSelection = () => {
    setSelectedItems(initialSelectedItems)
    postsQuery.refetch()
    soundsQuery.refetch()
  }

  const isMediaLoaded = postsQuery.isSuccess && soundsQuery.isSuccess

  const openEditModal = () => {
    openDialog({
      type: DIALOG_TYPE.CREATE_OR_EDIT_TIKTOK_LIST_DIALOG,
      list,
      isPersistent: true,
    })
  }

  const openDeleteModal = () => {
    openDialog({
      type: DIALOG_TYPE.DELETE_TIKTOK_LIST_DIALOG,
      list: list,
    })
  }

  if (isFetching || !list || !isMediaLoaded) {
    return <TikTokListDetailsSkeleton />
  }

  const videosCount = postsQuery.data.pages[0].totalElements
  const soundsCount = soundsQuery.data.pages[0].totalElements

  const pageDescription = [
    videosCount && `${videosCount} Videos`,
    soundsCount && `${soundsCount} Sounds`,
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <Page
      title={
        <ListLabel
          list={list}
          className="space-x-3 text-2xl font-semibold"
        />
      }
      description={pageDescription}
      backToPage={{
        url: AppRoutes.TiktokResearch.Lists.Index.asRouteDescriptor(),
        name: 'My List',
      }}
      headerOptions={
        <div className="flex gap-4">
          <Button
            variant="outline"
            className="border-primary-500 text-primary-500 hover:border-primary-500 hover:bg-primary-500 dark:border-primary-500 dark:hover:border-primary-500 dark:hover:bg-primary-500 bg-transparent hover:text-white"
            onClick={openEditModal}
          >
            <MdEdit className="mr-1 h-4 w-4" />
            Edit List
          </Button>
          <Button
            variant="outline"
            className="border-red-500 bg-transparent text-red-500 hover:border-red-500 hover:bg-red-500 hover:text-white dark:border-red-500 dark:hover:border-red-500 dark:hover:bg-red-500"
            onClick={openDeleteModal}
          >
            <MdDelete className="mr-1 h-4 w-4" />
            Delete List
          </Button>
        </div>
      }
    >
      <div className="w-full space-y-6 overflow-hidden">
        <ListItemsGroup
          title="Videos"
          mediaType="posts"
          selectedItems={selectedItems.posts}
          onSelectItem={handleSelectItem}
          query={postsQuery}
          listId={listId}
        />
        <ListItemsGroup
          title="Sounds"
          mediaType="sounds"
          selectedItems={selectedItems.sounds}
          onSelectItem={handleSelectItem}
          query={soundsQuery}
          listId={listId}
        />
      </div>
      <SelectedItemsActions
        selectedItems={selectedItems}
        currentList={list}
        onActionSuccess={resetSelection}
      />
    </Page>
  )
}
